import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MdAttachFile, MdChecklist, MdClose, MdLabel, MdLock, MdLockOpen, MdDeleteOutline, MdPersonAdd, MdSwapHoriz, MdWarning } from "react-icons/md";
import { toast } from "react-toastify";
import Modal from "../../../../components/modal";
import SearchableSelect from "../../../../components/searchableSelect/SearchableSelect";
import TicketCategoryPicker from "../../../../components/ticketCategory/TicketCategoryPicker";
import { resolveCategoryPick } from "../../../../components/ticketCategory/categoryPick";
import {
  Ticket,
  TicketCategory,
  TicketAttachment,
  TicketComment,
  TicketQueue,
  TicketState,
  TICKET_CHANNEL_LABELS,
  TICKET_STATE_LABELS,
} from "../../../../interfaces/Ticket";
import {
  assignTicket,
  changeTicketState,
  deleteTicket,
  getTicket,
  getTicketAttachments,
  getTicketComments,
  downloadTicketAttachment,
  moveTicketQueue,
  setTicketCategory,
} from "../../../../services/ticketApi";
import { subscribeToTicket } from "../../../../services/ticketHub";
import {
  TicketImpactBadge,
  TicketPriorityBadge,
  TicketStateBadge,
  TicketUrgencyBadge,
} from "./TicketBadges";
import TicketComments from "./TicketComments";
import AnexosLista from "../../../../components/anexos/AnexosLista";

interface EmployeeOption {
  id: number;
  name: string;
}

interface Props {
  ticketId: number;
  employees: EmployeeOption[];
  queues: TicketQueue[];
  categories: TicketCategory[];
  myEmployeeId: number | null;
  canManage: boolean;
  /** `tickets.admin` — habilita "Excluir chamado". */
  canAdmin?: boolean;
  onClose: () => void;
  /** Chamado sempre que algo muda no chamado (assumir, fila, estado, comentário) — recarrega a lista. */
  onChanged: () => void;
}

const fmtDateTime = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

/**
 * Detalhe do chamado: dados, ações de atendimento (assumir, mover de fila, mudar estado, fechar,
 * reabrir) e a thread de comentários. Uma tela só — abrir um modal à parte para cada ação faria a
 * pessoa perder o contexto do chamado a cada clique.
 */
const TicketDetailModal: React.FC<Props> = ({
  ticketId,
  employees,
  queues,
  categories,
  myEmployeeId,
  canManage,
  canAdmin = false,
  onClose,
  onChanged,
}) => {
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [comments, setComments] = useState<TicketComment[]>([]);
  const [attachments, setAttachments] = useState<TicketAttachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const [assigneePick, setAssigneePick] = useState<number | null>(null);
  const [queuePick, setQueuePick] = useState<number | null>(null);
  const [statePick, setStatePick] = useState<number | null>(null);
  const [categoryPick, setCategoryPick] = useState<number | null>(null);

  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!ticket || !deleteReason.trim()) return;
    setDeleting(true);
    try {
      await deleteTicket(ticket.id, deleteReason.trim());
      toast.success("Chamado excluído.");
      onChanged();
      onClose();
    } catch (e: any) {
      // 400/409: a mensagem do servidor diz o porquê (ex.: tem horas lançadas); o modal fica aberto.
      toast.error(e?.response?.data?.message ?? "Não foi possível excluir o chamado.");
    } finally {
      setDeleting(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const [t, c, anexos] = await Promise.all([
        getTicket(ticketId),
        getTicketComments(ticketId),
        // Anexo é opcional: com `Itsm:Attachments:Enabled=false` a lista vem vazia, e uma falha
        // aqui não pode impedir o chamado de abrir na tela.
        getTicketAttachments(ticketId).catch(() => []),
      ]);
      setTicket(t);
      setComments(c);
      setAttachments(anexos);
      setAssigneePick(t.assigneeEmployeeId ?? null);
      setQueuePick(t.queueId ?? null);
      setStatePick(null);
      setCategoryPick(t.categoryId ?? null);
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? "Não foi possível carregar o chamado.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId]);

  /**
   * Tempo real da thread: comentário público publicado por outra pessoa aparece sem recarregar.
   *
   * ⚠ Não reusa o `onCommentAdded` abaixo de propósito. Aquele dispara `onChanged()`, que recarrega
   * a LISTA de chamados inteira — comportamento certo para quem acabou de escrever, e caro demais
   * para um evento que pode chegar a qualquer momento.
   *
   * ⚠ Dedupe por id: quem escreveu o comentário já o inseriu de forma otimista em
   * `TicketComments`, e o push chega em seguida com o mesmo comentário. Sem isto, o autor vê a
   * própria mensagem duas vezes.
   *
   * O modal só é montado com um chamado selecionado, então fechar desmonta de verdade e a limpeza
   * abaixo é ponto de desconexão confiável.
   */
  useEffect(() => {
    if (!ticketId) return;

    const desinscrever = subscribeToTicket(ticketId, (novo) => {
      setComments((anteriores) =>
        anteriores.some((c) => c.id === novo.id) ? anteriores : [...anteriores, novo]
      );
    });

    return desinscrever;
  }, [ticketId]);

  // Só oferece os destinos que o servidor já disse que aceita (`TicketStates.AllowedFrom`) — a
  // regra continua lá, isto só evita um botão que sabidamente vai dar 400.
  const stateOptions = useMemo(
    () =>
      (ticket?.allowedStates ?? []).map((s) => ({ label: TICKET_STATE_LABELS[s] ?? `Estado ${s}`, value: s })),
    [ticket?.allowedStates]
  );

  // A categoria do chamado saiu do catálogo (foi desativada depois de ele ser classificado). O
  // seletor só oferece as ATIVAS, então ele mostraria "Sem categoria" para um chamado que TEM uma —
  // e o botão ficaria habilitado como se houvesse uma mudança pendente que ninguém pediu.
  const categoriaForaDoCatalogo =
    ticket?.categoryId != null &&
    resolveCategoryPick(categories, ticket.categoryId).rootId == null;

  const runAction = async (action: () => Promise<void>, successMsg: string) => {
    setBusy(true);
    try {
      await action();
      await load();
      onChanged();
      toast.success(successMsg);
    } catch (e: any) {
      // A mensagem do servidor é a autoridade sobre transições permitidas (CanTransition) —
      // engolir aqui deixaria "cliquei e não aconteceu nada" sem nenhuma pista do motivo.
      toast.error(e?.response?.data?.message ?? "Não foi possível concluir a ação.");
    } finally {
      setBusy(false);
    }
  };

  const handleAssumeForMe = () => {
    if (myEmployeeId == null || !ticket) return;
    runAction(() => assignTicket(ticket.id, myEmployeeId), "Chamado assumido.");
  };

  const handleAssignPicked = () => {
    if (assigneePick == null || !ticket) return;
    runAction(() => assignTicket(ticket.id, assigneePick), "Responsável atualizado.");
  };

  const handleMoveQueue = () => {
    if (!ticket) return;
    runAction(() => moveTicketQueue(ticket.id, queuePick), "Chamado movido de fila.");
  };

  const handleReclassify = () => {
    if (!ticket) return;
    runAction(() => setTicketCategory(ticket.id, categoryPick), "Chamado reclassificado.");
  };

  const handleApplyState = () => {
    if (statePick == null || !ticket) return;
    runAction(() => changeTicketState(ticket.id, statePick as TicketState), "Estado atualizado.");
  };

  const handleClose = () => {
    if (!ticket) return;
    if (!window.confirm(`Fechar o chamado #${ticket.number}?`)) return;
    runAction(() => changeTicketState(ticket.id, TicketState.Fechado), "Chamado fechado.");
  };

  const handleReopen = () => {
    if (!ticket) return;
    runAction(() => changeTicketState(ticket.id, TicketState.Reaberto), "Chamado reaberto.");
  };

  // Anexos de mensagem aparecem na bolha; aqui ficam só os do chamado (abertura, e-mail inicial).
  const anexosDoChamado = attachments.filter((a) => a.commentId == null);

  const baixarAnexo = async (a: TicketAttachment) => {
    try {
      await downloadTicketAttachment(a.id, a.fileName);
    } catch {
      // O servidor recusa com 404 tanto para "não existe" quanto para "é de outro cliente", de
      // propósito — então não há o que distinguir aqui além de avisar que não deu.
      toast.error("Não foi possível baixar o anexo.");
    }
  };

  const onCommentAdded = (comment: TicketComment) => {
    setComments((prev) => [...prev, comment]);
    onChanged();
  };

  const inputCls =
    "w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 dark:border-gray-600 dark:bg-navy-900 dark:text-white";
  const labelCls = "mb-1 block text-xs font-semibold text-gray-600 dark:text-gray-400";

  return (
    <Modal onClose={onClose}>
      <div
        className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-[20px] border border-gray-100 bg-white shadow-dv-lg dark:border-white/10 dark:bg-navy-800"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4 dark:border-white/10">
          <h3 className="text-lg font-bold text-navy-700 dark:text-white">
            {ticket ? `#${ticket.number}` : "Chamado"}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="rounded-lg p-1 text-gray-700 transition-colors hover:bg-gray-100 hover:text-navy-700 dark:text-gray-300 dark:hover:bg-navy-700 dark:hover:text-white"
          >
            <MdClose className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {loading || !ticket ? (
            <div className="py-14 text-center text-sm text-gray-500 dark:text-gray-400">
              Carregando…
            </div>
          ) : (
            <div className="flex flex-col gap-5">
              {/* Cabeçalho: assunto, cliente, badges */}
              <div>
                <h4 className="text-xl font-bold text-navy-700 dark:text-white">{ticket.subject}</h4>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                  {ticket.clientName ?? `Cliente #${ticket.clientId}`}
                  {ticket.requesterEmail ? ` · ${ticket.requesterEmail}` : ""}
                  {" · "}
                  {TICKET_CHANNEL_LABELS[ticket.channel] ?? ticket.channel}
                </p>
                <p className="mt-1 inline-flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400">
                  <MdLabel className="h-4 w-4 text-gray-400" aria-hidden="true" />
                  {ticket.categoryPath ?? (
                    <span className="italic text-gray-400 dark:text-gray-500">Sem categoria</span>
                  )}
                </p>
                <div className="mt-2.5 flex flex-wrap gap-2">
                  <TicketStateBadge state={ticket.state} />
                  <TicketPriorityBadge priority={ticket.priority} />
                  <TicketImpactBadge impact={ticket.impact} />
                  <TicketUrgencyBadge urgency={ticket.urgency} />
                  {ticket.slaResolutionBreached && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-1 text-[11px] font-bold text-red-700 dark:bg-red-900/40 dark:text-red-300">
                      <MdWarning className="h-3.5 w-3.5" aria-hidden="true" />
                      SLA estourado
                    </span>
                  )}
                </div>
                <p className="mt-3 whitespace-pre-wrap rounded-xl bg-gray-50 p-3 text-sm text-gray-700 dark:bg-navy-900 dark:text-gray-300">
                  {ticket.description}
                </p>
              </div>

              {/* Datas */}
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 rounded-xl border border-gray-100 p-3.5 text-xs dark:border-white/10 sm:grid-cols-4">
                <div>
                  <p className="font-semibold text-gray-500 dark:text-gray-400">Aberto em</p>
                  <p className="text-gray-800 dark:text-gray-200">{fmtDateTime(ticket.openedAt)}</p>
                </div>
                <div>
                  <p className="font-semibold text-gray-500 dark:text-gray-400">1ª resposta</p>
                  <p className="text-gray-800 dark:text-gray-200">{fmtDateTime(ticket.firstResponseAt)}</p>
                </div>
                <div>
                  <p className="font-semibold text-gray-500 dark:text-gray-400">Resolvido em</p>
                  <p className="text-gray-800 dark:text-gray-200">{fmtDateTime(ticket.resolvedAt)}</p>
                </div>
                <div>
                  <p className="font-semibold text-gray-500 dark:text-gray-400">Fechado em</p>
                  <p className="text-gray-800 dark:text-gray-200">{fmtDateTime(ticket.closedAt)}</p>
                </div>
                {ticket.reopenCount > 0 && (
                  <div className="col-span-2 sm:col-span-4">
                    <p className="font-semibold text-amber-600 dark:text-amber-400">
                      Reaberto {ticket.reopenCount}x
                    </p>
                  </div>
                )}
              </div>

              {/* Horas e tarefa — o trabalho mora na tarefa (design D1), aqui só o resumo + o link */}
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-100 p-3.5 text-xs dark:border-white/10">
                <div>
                  <p className="font-semibold text-gray-500 dark:text-gray-400">Horas (estimado / realizado)</p>
                  <p className="text-gray-800 dark:text-gray-200">
                    est {ticket.estimatedHours ?? 0}h
                    <span className="mx-1 text-gray-400" aria-hidden="true">
                      ·
                    </span>
                    <span
                      className={
                        (ticket.estimatedHours ?? 0) > 0 && ticket.workedHours > (ticket.estimatedHours ?? 0)
                          ? "font-semibold text-red-600 dark:text-red-400"
                          : "font-semibold text-emerald-600 dark:text-emerald-400"
                      }
                    >
                      real {ticket.workedHours}h
                    </span>
                  </p>
                </div>
                {/*
                  O id vai no `state`, não só no tooltip: sem ele a tela de Tarefas abria com TODAS
                  as tarefas de todos os projetos e a pessoa tinha que achar a do chamado na mão.
                  `location.state` é o precedente que já existe na base (Financeiro → Projetos),
                  e o `projectId` viaja junto para a tela poder filtrar o projeto e garantir que a
                  tarefa esteja na lista carregada.
                */}
                <Link
                  to="/admin/project-tasks"
                  state={{ openTaskId: ticket.projectTaskId, projectId: ticket.projectId }}
                  className="flex items-center gap-1.5 rounded-xl border border-gray-200 px-3 py-2 text-xs font-semibold text-gray-700 transition-all hover:bg-gray-50 dark:border-white/10 dark:text-gray-200 dark:hover:bg-navy-700"
                  title={`Projeto #${ticket.projectId} · Tarefa #${ticket.projectTaskId}`}
                >
                  <MdChecklist className="h-4 w-4" aria-hidden="true" />
                  Abrir a tarefa deste chamado
                </Link>
              </div>

              {/* Ações de atendimento */}
              {canManage && (
                <div className="flex flex-col gap-3 rounded-xl border border-gray-100 p-4 dark:border-white/10">
                  <p className="text-sm font-bold text-navy-700 dark:text-white">Atendimento</p>

                  {/* Responsável */}
                  <div className="flex flex-col gap-1.5">
                    <span className={labelCls}>Responsável</span>
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="min-w-[200px] flex-1">
                        <SearchableSelect
                          placeholder="— Ninguém —"
                          value={assigneePick}
                          onChange={(v) => setAssigneePick(v == null ? null : Number(v))}
                          options={employees.map((e) => ({ label: e.name, value: e.id }))}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={handleAssignPicked}
                        disabled={busy || assigneePick == null || assigneePick === ticket.assigneeEmployeeId}
                        className="flex items-center gap-1.5 rounded-xl border border-gray-200 px-3.5 py-2 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-white/10 dark:text-gray-200 dark:hover:bg-navy-700"
                      >
                        <MdPersonAdd className="h-4 w-4" aria-hidden="true" />
                        Atribuir
                      </button>
                      {myEmployeeId != null && ticket.assigneeEmployeeId !== myEmployeeId && (
                        <button
                          type="button"
                          onClick={handleAssumeForMe}
                          disabled={busy}
                          className="rounded-xl bg-ink-900 px-3.5 py-2 text-sm font-bold text-white transition-all hover:bg-ink-800 disabled:cursor-not-allowed disabled:opacity-40 dark:bg-white dark:text-ink-900 dark:hover:bg-gray-100"
                        >
                          Assumir para mim
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Fila */}
                  <div className="flex flex-col gap-1.5">
                    <span className={labelCls}>Fila</span>
                    <div className="flex flex-wrap items-center gap-2">
                      <div className="min-w-[200px] flex-1">
                        <SearchableSelect
                          placeholder="— Sem fila —"
                          value={queuePick}
                          onChange={(v) => setQueuePick(v == null ? null : Number(v))}
                          options={queues.map((q) => ({ label: q.name, value: q.id }))}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={handleMoveQueue}
                        disabled={busy || queuePick === (ticket.queueId ?? null)}
                        className="flex items-center gap-1.5 rounded-xl border border-gray-200 px-3.5 py-2 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-white/10 dark:text-gray-200 dark:hover:bg-navy-700"
                      >
                        <MdSwapHoriz className="h-4 w-4" aria-hidden="true" />
                        Mover
                      </button>
                    </div>
                  </div>

                  {/* Categoria — reclassificar é atender (`tickets.manage`); mexer no CATÁLOGO é
                      configurar o módulo e mora na tela de Categorias (`tickets.admin`). */}
                  <div className="flex flex-col gap-1.5">
                    <span className={labelCls}>Categoria</span>
                    <div className="flex flex-wrap items-end gap-2">
                      <div className="min-w-[260px] flex-1">
                        <TicketCategoryPicker
                          categories={categories}
                          value={categoryPick}
                          onChange={setCategoryPick}
                          disabled={busy}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={handleReclassify}
                        disabled={busy || categoryPick === (ticket.categoryId ?? null) || categoriaForaDoCatalogo}
                        className="flex items-center gap-1.5 rounded-xl border border-gray-200 px-3.5 py-2 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-white/10 dark:text-gray-200 dark:hover:bg-navy-700"
                      >
                        <MdLabel className="h-4 w-4" aria-hidden="true" />
                        Reclassificar
                      </button>
                    </div>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400">
                      {categoriaForaDoCatalogo ? (
                        <span className="text-amber-600 dark:text-amber-400">
                          Este chamado está em <strong>{ticket.categoryPath}</strong>, que foi
                          desativada e por isso não aparece na lista. Reative-a em Categorias para
                          poder reclassificar sem perder a classificação atual.
                        </span>
                      ) : (
                        <>Recalcula o prazo do SLA. A fila não muda junto — use "Mover" para isso.</>
                      )}
                    </p>
                  </div>

                  {/* Estado */}
                  <div className="flex flex-col gap-1.5">
                    <span className={labelCls}>Mudar estado</span>
                    <div className="flex flex-wrap items-center gap-2">
                      <select
                        className={`${inputCls} max-w-[240px]`}
                        value={statePick ?? ""}
                        onChange={(e) => setStatePick(e.target.value ? Number(e.target.value) : null)}
                      >
                        <option value="">Selecione o novo estado…</option>
                        {stateOptions.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        onClick={handleApplyState}
                        disabled={busy || statePick == null}
                        className="rounded-xl border border-gray-200 px-3.5 py-2 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-white/10 dark:text-gray-200 dark:hover:bg-navy-700"
                      >
                        Aplicar
                      </button>
                    </div>
                  </div>

                  {/* Fechar / reabrir — atalhos para as duas transições mais comuns. Só aparecem
                      quando o servidor já disse (via allowedStates) que aceita a transição — mesma
                      regra do seletor genérico acima, para não oferecer um atalho que dá 400. */}
                  {(ticket.state !== TicketState.Fechado
                    ? ticket.allowedStates.includes(TicketState.Fechado)
                    : ticket.allowedStates.includes(TicketState.Reaberto)) && (
                    <div className="flex gap-2.5 border-t border-gray-100 pt-3 dark:border-white/10">
                      {ticket.state !== TicketState.Fechado ? (
                        <button
                          type="button"
                          onClick={handleClose}
                          disabled={busy}
                          className="flex items-center gap-1.5 rounded-xl border border-red-200 px-4 py-2 text-sm font-semibold text-red-600 transition-all hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-red-500/30 dark:text-red-400 dark:hover:bg-red-500/10"
                        >
                          <MdLock className="h-4 w-4" aria-hidden="true" />
                          Fechar chamado
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={handleReopen}
                          disabled={busy}
                          className="flex items-center gap-1.5 rounded-xl border border-emerald-200 px-4 py-2 text-sm font-semibold text-emerald-600 transition-all hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-emerald-500/30 dark:text-emerald-400 dark:hover:bg-emerald-500/10"
                        >
                          <MdLockOpen className="h-4 w-4" aria-hidden="true" />
                          Reabrir chamado
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Exclusão: só tickets.admin; discreta de propósito (ação rara e destrutiva) */}
              {canAdmin && (
                <div className="flex flex-col gap-2 border-t border-gray-100 pt-4 dark:border-white/10">
                  {!confirmingDelete ? (
                    <div className="flex flex-wrap items-center gap-3">
                      <button
                        type="button"
                        onClick={() => setConfirmingDelete(true)}
                        disabled={ticket.workedHours > 0}
                        className="flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-sm font-semibold text-red-600 transition-all hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40 dark:text-red-400 dark:hover:bg-red-500/10"
                      >
                        <MdDeleteOutline className="h-4 w-4" aria-hidden="true" />
                        Excluir chamado
                      </button>
                      {ticket.workedHours > 0 && (
                        <span className="text-xs text-gray-600 dark:text-gray-400">
                          Tem horas lançadas — feche o chamado em vez de excluir.
                        </span>
                      )}
                    </div>
                  ) : (
                    <div
                      role="group"
                      aria-label="Confirmar exclusão do chamado"
                      className="flex flex-col gap-3 rounded-xl border border-red-200 bg-red-50/60 p-4 dark:border-red-500/30 dark:bg-red-500/10"
                    >
                      <p className="text-sm text-red-700 dark:text-red-300">
                        O chamado some da lista, do portal do cliente e do indicador de SLA. A tarefa ligada a
                        ele no projeto também é removida.
                      </p>
                      <div>
                        <label htmlFor="ticket-delete-reason" className={labelCls}>
                          Motivo
                        </label>
                        <textarea
                          id="ticket-delete-reason"
                          rows={2}
                          value={deleteReason}
                          onChange={(e) => setDeleteReason(e.target.value)}
                          className={inputCls}
                        />
                        <div className="mt-2 flex flex-wrap gap-2">
                          {["Aberto por erro", "Teste"].map((chip) => (
                            <button
                              key={chip}
                              type="button"
                              onClick={() => setDeleteReason(chip)}
                              className="rounded-full border border-gray-200 px-3 py-1 text-xs font-semibold text-gray-700 hover:bg-white dark:border-white/15 dark:text-gray-200 dark:hover:bg-white/5"
                            >
                              {chip}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="flex gap-2.5">
                        <button
                          type="button"
                          onClick={() => {
                            setConfirmingDelete(false);
                            setDeleteReason("");
                          }}
                          disabled={deleting}
                          className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-white disabled:opacity-40 dark:border-white/15 dark:text-gray-200 dark:hover:bg-white/5"
                        >
                          Cancelar
                        </button>
                        <button
                          type="button"
                          onClick={() => void handleDelete()}
                          disabled={deleting || !deleteReason.trim()}
                          className="rounded-xl bg-red-600 px-4 py-2 text-sm font-bold text-white transition-all hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          {deleting ? "Excluindo…" : "Excluir"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Comentários */}
              <div>
                <p className="mb-3 text-sm font-bold text-navy-700 dark:text-white">Comentários</p>
                {anexosDoChamado.length > 0 && (
                  <div className="mb-4 rounded-xl border border-gray-100 p-3.5 dark:border-white/10">
                    <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400">
                      <MdAttachFile className="h-4 w-4" aria-hidden="true" />
                      Anexos ({anexosDoChamado.length})
                    </p>
                    <AnexosLista anexos={anexosDoChamado} onBaixar={(a) => void baixarAnexo(a)} />
                  </div>
                )}

                <TicketComments
                  ticketId={ticket.id}
                  comments={comments}
                  canComment={canManage}
                  onAdded={onCommentAdded}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default TicketDetailModal;
